// BrandStep — brand picker for the AddItem wizard.
//
// Two-tier UI:
//   1. Brand pills (Pepsi, Coca-Cola, Cheerios, Lay's, Häagen-Dazs, …)
//      — the brand "family" the user identifies on the shelf.
//   2. Variant pills (Pepsi → Original / Zero Sugar / Wild Cherry / Vanilla …)
//      — the product line within that family. Variants only render
//      after a brand with `variants` is picked.
//
// Variant binding: the committed brand string is "{base} {variant}"
// — e.g. "Pepsi Wild Cherry", "Honey Nut Cheerios", "Lay's Sour Cream
// & Onion". This keeps brand_nutrition keyed on the exact product
// (Diet Coke 0kcal vs regular 140kcal — wildly different macros, must
// not collide). Skipping the variant step commits just "{base}".
//
// Brand pills source from the five-tier merge in `brandsForCanonical`:
//   1. BRAND_EXPERTISE        (curated)
//   2. BRAND_EXPERTISE_FROM_USDA (derived)
//   3. KNOWN_BRANDS canonicalHint
//   4. live observations from manual_corrections
//   5. AI-suggested common brands (info.brands.common)
//
// Region filter (useShoppingRegion) filters Tier 5 brands so a
// Chicago user sees Vienna Beef and a Berlin user sees Augustiner —
// neither sees the other. Other tiers pass through unfiltered.

import { useEffect, useMemo, useState } from "react";
import { brandsForCanonical, BRAND_EXPERTISE, brandSlugify } from "../../../data/brandExpertise";
import { useBrandObservations } from "../../../lib/useBrandObservations";
import { useBrandsFromPantry } from "../../../lib/useBrandsFromPantry";
import { useIngredientInfo } from "../../../lib/useIngredientInfo";
import { useBrandIdentity } from "../../../lib/useBrandIdentity";
import { useShoppingRegion } from "../../../lib/useShoppingRegion";
import { useActiveStore } from "../../../lib/useActiveStore";
import { findIngredient, findHub } from "../../../data/ingredients";
import { brandAvailabilityHint } from "../../../lib/brandAvailabilityHint";
import RegionalEnrichmentBanner from "../../../components/RegionalEnrichmentBanner";
import { font, color, radius } from "../../../lib/mcmTokens";
import { CHIP_TONES } from "../../../lib/tokens";

const accent = CHIP_TONES.canonical.fg;

/**
 * Props:
 *   canonicalId  — required; drives the brand merge
 *   value        — current brand string ("Pepsi", parent only)
 *   variantValue — current variant string ("Wild Cherry") or null
 *   onChange     — (brand: string|null) => void; fires on brand-pill pick
 *   onVariant    — (variant: string|null) => void; fires on variant-pill pick
 *   onCommit     — () => void; fires after a brand+variant are chosen (advance)
 *   onSkip       — () => void; fires when user opts out of brand
 */
export default function BrandStep({
  canonicalId,
  value = null,
  variantValue = null,
  onChange,
  onVariant,
  onCommit,
  onSkip,
  // When false, picking a brand with variants does NOT show the
  // variant row inline — the consumer drives a separate variant
  // step. WizardPipeline uses this; the older inline form (where
  // brand+variant share one step) keeps the default true.
  inlineVariants = true,
}) {
  const { getInfo } = useIngredientInfo();
  const { observations: rawObservations } = useBrandObservations(canonicalId);
  // Pull the user's own pantry history for this canonical. Closes
  // the "I scanned Ski Queen for gjetost, why isn't it in the
  // picker" gap — manual_corrections only captures admin-tier
  // corrections, but pantry_items has every brand the user has
  // ever added. Merge the two so the user's scan/add history
  // becomes a first-class observation source.
  const pantryBrands = useBrandsFromPantry(canonicalId);
  const observations = useMemo(() => {
    const out = [...(Array.isArray(rawObservations) ? rawObservations : [])];
    const seenSlugs = new Set(out.map(o => (o.slug || (o.brand || "").toLowerCase().trim())));
    for (const p of pantryBrands || []) {
      const key = (p.brand || "").toLowerCase().trim();
      if (!key || seenSlugs.has(key)) continue;
      out.push(p);
      seenSlugs.add(key);
    }
    return out;
  }, [rawObservations, pantryBrands]);
  const { bySlug: brandIdentities } = useBrandIdentity();
  const [region] = useShoppingRegion();
  const [activeStore] = useActiveStore();
  const [typed, setTyped] = useState(value || "");

  // The base brand the user picked first (if any). When set, the
  // variant pill row appears. null = no brand picked yet OR the
  // user typed a custom brand without picking a pill.
  const [activeBase, setActiveBase] = useState(null);

  useEffect(() => {
    if (value && value !== typed) setTyped(value);
  }, [value]);  // eslint-disable-line react-hooks/exhaustive-deps

  const suggestions = useMemo(() => {
    if (!canonicalId) return [];
    const info = getInfo?.(canonicalId) || null;
    return brandsForCanonical(canonicalId, {
      limit: 12,
      observations,
      info,
      region,
      brandIdentities,
      // Mid-trip store context — boosts the store's house brands
      // ("at Costco, Kirkland first"). Null outside a trip.
      store: activeStore,
    });
  }, [canonicalId, observations, getInfo, region, brandIdentities, activeStore]);

  // Specialty slug set — these brands surface above "Common local
  // brands". Two membership signals:
  //   1. The user (or their family) has typed/scanned this brand for
  //      this canonical before — comes through `observations` from
  //      useBrandsFromPantry / useBrandObservations.
  //   2. Curated BRAND_EXPERTISE has an explicit `canonicals[id] > 0`
  //      mapping for this canonical — i.e. someone hand-stamped this
  //      brand as a known producer of this exact canonical.
  // Tier 5 (AI common brands) and Tier 1.5/2/3 (subtype-claim, USDA,
  // KNOWN_BRANDS) are intentionally NOT in this set — they're broader
  // hub-level guesses that belong in the common-brands section.
  const specialtySlugs = useMemo(() => {
    const set = new Set();
    for (const obs of observations || []) {
      const display = obs?.display || obs?.brand || null;
      const slug = obs?.slug || (display ? brandSlugify(display) : null);
      if (slug) set.add(slug);
    }
    if (canonicalId) {
      for (const [slug, rec] of Object.entries(BRAND_EXPERTISE)) {
        if (Number(rec?.canonicals?.[canonicalId] || 0) > 0) set.add(slug);
      }
    }
    return set;
  }, [observations, canonicalId]);

  const specialtySuggestions = useMemo(
    () => suggestions.filter((s) => specialtySlugs.has(s.slug)),
    [suggestions, specialtySlugs],
  );
  const commonSuggestions = useMemo(
    () => suggestions.filter((s) => !specialtySlugs.has(s.slug)),
    [suggestions, specialtySlugs],
  );

  // The variant list of the currently-active base brand (if any).
  // null = no variants to show. Empty array also collapses the row.
  const activeVariants = useMemo(() => {
    if (!activeBase) return null;
    const hit = suggestions.find((s) => s.slug === activeBase.slug);
    if (!hit?.variants) return null;
    return hit.variants;
  }, [activeBase, suggestions]);

  // Heads-up notice for the active brand's availability — fires for
  // store-exclusives ("Only at Costco"), online-only ("Online only —
  // try Amazon"), specialty/natural-only ("Usually only at Whole
  // Foods, Sprouts"), region-locked ("Mostly stocked in St. Louis"),
  // or store-mismatch ("Your usual stores don't carry this — try X").
  // Returns null for mainstream brands so the UI stays quiet when
  // there's nothing useful to flag.
  const availabilityHint = useMemo(() => {
    if (!activeBase) return null;
    return brandAvailabilityHint(activeBase, { region });
  }, [activeBase, region]);

  function pickBrandPill(s) {
    setTyped(s.display);
    onChange?.(s.display);
    // Picking a parent brand resets any prior variant — Pepsi → Coke
    // shouldn't carry "Wild Cherry" across.
    onVariant?.(null);
    if (Array.isArray(s.variants) && s.variants.length > 0) {
      if (inlineVariants) {
        // Inline mode (legacy): stay on this step — variant pills
        // render below the brand pills.
        setActiveBase(s);
      } else {
        // Step-based mode (wizard): commit immediately. The parent
        // routes to a separate variant step based on whether
        // s.variants is non-empty (it can re-derive that from the
        // committed brand value via brandsForCanonical).
        setActiveBase(null);
        onCommit?.();
      }
    } else {
      // No variants — auto-advance regardless of mode.
      setActiveBase(null);
      onCommit?.();
    }
  }

  function pickVariant(v) {
    // Bind the variant axis SEPARATELY from brand. brand stays
    // "Pepsi" — variant becomes "Wild Cherry". Display composes at
    // render time. brand_nutrition lookup uses the (canonical, brand,
    // variant) tuple so Diet Coke and regular Coke can hold distinct
    // nutrition without colliding.
    onVariant?.(v.name);
    onCommit?.();
  }

  function commitTyped() {
    const t = typed.trim();
    if (!t) return;
    onChange?.(t);
    onCommit?.();
  }

  const canCommit = typed.trim().length > 0;
  const activeSlug = (() => {
    if (!value) return null;
    const lower = value.trim().toLowerCase();
    // Exact match first (covers the no-variant case).
    const direct = suggestions.find((s) => s.display.toLowerCase() === lower);
    if (direct) return direct.slug;
    // Fall back to prefix match for variant strings ("Pepsi Wild Cherry"
    // → "pepsi"). Highlights the parent pill so the user sees their
    // base selection even after a variant pick.
    const prefix = suggestions.find((s) =>
      lower.startsWith(s.display.toLowerCase() + " ")
      || lower.startsWith(s.display.toLowerCase())
    );
    return prefix?.slug || null;
  })();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Regional enrichment banner — only renders when the user's
          primary city is mid-Haiku-pass. Tells them new local
          brands are being gathered and to check back later, with
          a live progress bar that ticks as canonicals process. */}
      <RegionalEnrichmentBanner compact />

      {suggestions.length > 0 && (() => {
        const ing = findIngredient(canonicalId);
        const hub = ing?.parentId ? findHub(ing.parentId) : null;
        const bucket =
          (hub?.name && String(hub.name).toLowerCase()) ||
          (ing?.category && String(ing.category).toLowerCase()) ||
          "this item";
        const canonName = ing?.name || canonicalId || "this item";
        const isLocalScoped = !!region;
        const commonLabel = isLocalScoped
          ? `Common local brands for ${bucket}`
          : `Common brands for ${bucket}`;
        const renderPill = (s) => {
          const active = s.slug === activeSlug;
          return (
            <button
              key={s.slug}
              onClick={(e) => { e.preventDefault(); pickBrandPill(s); }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                padding: "6px 12px",
                fontFamily: font.itemTitle,
                fontStyle: "italic",
                fontSize: 14,
                fontWeight: 500,
                color: active ? color.cream : color.ink,
                background: active ? accent : `${accent}1f`,
                border: `1px solid ${active ? accent : `${accent}55`}`,
                borderRadius: 999,
                cursor: "pointer",
              }}
            >
              {s.display}
            </button>
          );
        };
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {/* Specialty tier — curated entries explicitly for this
                canonical PLUS brands the user/family has typed before.
                Renders ABOVE common so user-curated picks always
                surface first regardless of AI weighting. */}
            {specialtySuggestions.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{
                  fontFamily: font.mono,
                  fontSize: 11,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: color.ink,
                }}>
                  {`Curated for ${canonName}`}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {specialtySuggestions.map(renderPill)}
                </div>
              </div>
            )}

            {commonSuggestions.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{
                  fontFamily: font.mono,
                  fontSize: 11,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: color.inkMuted,
                }}>
                  {commonLabel}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {commonSuggestions.map(renderPill)}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Availability heads-up — surfaces only when the brand is hard
          to find (exclusive / online-only / specialty / region-locked).
          Mainstream brands stay quiet. Non-blocking — informational. */}
      {availabilityHint && (
        <div style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          padding: "10px 12px",
          background: `${CHIP_TONES.canonical.fg}14`,
          border: `1px solid ${CHIP_TONES.canonical.fg}40`,
          borderRadius: radius.field || 12,
          fontFamily: font.detail,
          fontStyle: "italic",
          fontSize: 13,
          color: color.ink,
          lineHeight: 1.35,
        }}>
          <span style={{
            fontFamily: font.mono,
            fontStyle: "normal",
            fontSize: 9,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: CHIP_TONES.canonical.fg,
            paddingTop: 1,
            flexShrink: 0,
          }}>
            FYI
          </span>
          <span style={{ flex: 1 }}>{availabilityHint.message}</span>
        </div>
      )}

      {/* Variant row — appears only in inline mode AND when the
          picked brand has variants. The wizard uses step-based mode
          (inlineVariants=false), so this block stays out of its way
          and the variants render in a dedicated step instead. */}
      {inlineVariants && activeVariants && activeVariants.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{
            fontFamily: font.mono,
            fontSize: 10,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: accent,
          }}>
            {activeBase?.display} variants
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {activeVariants.map((v) => {
              const active = (variantValue || "").toLowerCase() === v.name.toLowerCase();
              return (
                <button
                  key={v.name}
                  onClick={(e) => { e.preventDefault(); pickVariant(v); }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "6px 12px",
                    fontFamily: font.itemTitle,
                    fontStyle: "italic",
                    fontSize: 13,
                    fontWeight: 500,
                    color: active ? color.cream : color.ink,
                    background: active
                      ? CHIP_TONES.ingredients.fg
                      : `${CHIP_TONES.ingredients.fg}1f`,
                    border: `1px solid ${active ? CHIP_TONES.ingredients.fg : `${CHIP_TONES.ingredients.fg}55`}`,
                    borderRadius: 999,
                    cursor: "pointer",
                  }}
                >
                  {v.name}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {suggestions.length === 0 && canonicalId && (
        <div style={{
          fontFamily: font.detail,
          fontStyle: "italic",
          fontSize: 13,
          color: color.inkMuted,
          padding: "4px 0",
        }}>
          No common brands yet — type one and it'll be remembered.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{
          fontFamily: font.mono,
          fontSize: 10,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          color: color.inkMuted,
        }}>
          Custom brand
        </div>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="Type a brand…"
          autoCapitalize="words"
          autoCorrect="off"
          style={{
            width: "100%",
            padding: "10px 12px",
            fontFamily: font.serif,
            fontStyle: "italic",
            fontSize: 16,
            color: color.ink,
            background: color.cream,
            border: `1px solid ${color.hairline}`,
            borderRadius: radius.field || 12,
            outline: "none",
          }}
        />
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button
          onClick={(e) => { e.preventDefault(); commitTyped(); }}
          disabled={!canCommit}
          style={{
            flex: 1,
            padding: "12px 16px",
            fontFamily: font.sans,
            fontSize: 14,
            fontWeight: 600,
            color: canCommit ? color.cream : color.inkFaint,
            background: canCommit ? color.ink : "transparent",
            border: `1px solid ${canCommit ? color.ink : color.hairline}`,
            borderRadius: radius.field || 12,
            cursor: canCommit ? "pointer" : "not-allowed",
          }}
        >
          Use this brand
        </button>
        <button
          onClick={(e) => { e.preventDefault(); onSkip?.(); }}
          style={{
            padding: "12px 16px",
            fontFamily: font.sans,
            fontSize: 13,
            color: color.inkMuted,
            background: "transparent",
            border: `1px solid ${color.hairline}`,
            borderRadius: radius.field || 12,
            cursor: "pointer",
          }}
        >
          Skip
        </button>
      </div>
    </div>
  );
}
